import { ConfigService } from '@stockcare-services/config-service/config.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IDevice } from '@stockcare-models/IDevice';
import { IKeyValue } from '@stockcare-models/IKeyValue';
import { DeviceType } from '@stockcare-models/DeviceType';
import { IHealthInformation } from '@stockcare-models/IHealthInformation';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  localDeviceUpdated = new Subject();
  theaterOperationInProgress = 'theater-operation-in-progress';

  constructor(private http: HttpClient, private conf: ConfigService) {}

  isDeviceRemoved(): boolean {
    const device = this.getDeviceFromLocalStorage();
    if (device && device.id && device.isRemoved) {
      return true;
    } else {
      return false;
    }
  }

  isDeviceLinked(): boolean {
    const device = this.getDeviceFromLocalStorage();
    if (device && device.id) {
      return true;
    } else {
      return false;
    }
  }

  updateThisDevice() {
    this.getDeviceById(this.getDeviceId()).subscribe((x) => {
      this.saveDeviceToLocalStorage(x);
    });
  }

  getAll(): Observable<IDevice[]> {
    return this.http.get<IDevice[]>(`${this.conf.config.baseUrl}/devices`);
  }

  getDeviceById(deviceId: number): Observable<IDevice> {
    return this.http.get<IDevice>(`${this.conf.config.baseUrl}/devices/${deviceId}`);
  }

  getDeviceTypes(): Observable<IKeyValue[]> {
    return this.http.get<IKeyValue[]>(`${this.conf.config.baseUrl}/devices/getDeviceTypes/`);
  }

  detectIsMobileDevice() {
    const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

    return toMatch.some((toMatchItem) => {
      return navigator.userAgent.match(toMatchItem);
    });
  }

  isWhiteboardDevice() {
    const device = this.getDeviceFromLocalStorage();
    if (device && device.deviceType === DeviceType.Whiteboard) {
      return true;
    }
    return false;
  }

  isTheaterDevice() {
    const device = this.getDeviceFromLocalStorage();
    if (
      device &&
      (device.deviceType === DeviceType.TheatreWaiting ||
        device.deviceType === DeviceType.TheatreOperatingRoom ||
        device.deviceType === DeviceType.TheatreRecovery || 
      device.deviceType === DeviceType.TheatreBackOffice)
    ) {
      return true;
    }
    return false;
  }
  
  isOriginalTheatreDevice() {
    const device = this.getDeviceFromLocalStorage();
    if (
      device &&
      (device.deviceType === DeviceType.TheatreWaiting ||
        device.deviceType === DeviceType.TheatreOperatingRoom ||
        device.deviceType === DeviceType.TheatreRecovery)
    ) {
      return true;
    }
    return false;
  }

  isEmergencyCupboardDevice() {
    const device = this.getDeviceFromLocalStorage();
    if (device && device.deviceType === DeviceType.EmergencyCupboard) {
      return true;
    }
    return false;
  }

  IsOperatingRoomDevice(): boolean {
    return this.getDeviceType() === DeviceType.TheatreOperatingRoom;
  }

  IsWaitingRoomDevice(): boolean {
    return this.getDeviceType() === DeviceType.TheatreWaiting;
  }

  IsRecoveryRoomDevice(): boolean {
    return this.getDeviceType() === DeviceType.TheatreRecovery;
  }

  isBackOfficeDevice() {
      const device = this.getDeviceFromLocalStorage();
      if (device && device.deviceType === DeviceType.BackOffice) {
          return true;
      }
      return false;
  }

  isTisScoringDevice() {
    const device = this.getDeviceFromLocalStorage();
    if (device && device.deviceType === DeviceType.TisScoring) {
        return true;
    }
    return false;
}

isTheatreBackOfficeDevice() {
  const device = this.getDeviceFromLocalStorage();
  if (device && device.deviceType === DeviceType.TheatreBackOffice) {
      return true;
  }
  return false;
}

  getDeviceType() {
    const device = this.getDeviceFromLocalStorage();
    return device == null ? null : device.deviceType;
  }

  create(device: IDevice): Observable<boolean> {
    return new Observable((observer) => {
      this.http.post<IDevice>(`${this.conf.config.baseUrl}/devices`, device).subscribe(
        (updatedDevice: IDevice) => {
          this.saveDeviceToLocalStorage(updatedDevice);
          observer.next(true);
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  update(device: IDevice): Observable<boolean> {
    return new Observable((observer) => {
      this.http.put<IDevice>(`${this.conf.config.baseUrl}/devices/${device.id}`, device).subscribe(
        (savedDevice: IDevice) => {
          const localDevice = this.getDeviceFromLocalStorage();
          if (!localDevice || (localDevice && localDevice.id === savedDevice.id)) {
            this.saveDeviceToLocalStorage(savedDevice);
          }
          observer.next(true);
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  removeDevice(device: IDevice): Observable<boolean> {
    return new Observable((observer) => {
      this.http.delete<string>(`${this.conf.config.baseUrl}/devices/${device.id}`).subscribe(
        () => {
          this.removeIfCurrentDevice(device);
          observer.next(true);
        },
        (error) => {
          observer.error(error);
        }
      );
    });
  }

  public getDeviceFromLocalStorage(): IDevice {
    const device = localStorage.getItem('device');
    return device != null ? JSON.parse(device) : null;
  }

  public getDeviceId(): number {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.id : 0;
  }

  getInstitutionId(): number {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.institutionId : 0;
  }

  getStockLocationId(): number {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.stockLocationId : 0;
  }

  getStockLocationName(): string {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.stockLocationName : '';
  }

  getPhysicalLocationId(): number {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.physicalLocationId : 0;
  }

  getPhysicalLocationRoom(): number {
    const device = this.getDeviceFromLocalStorage();
    return device != null ? device.physicalLocationTheatreRoomId : 0;
  }

  saveDeviceToLocalStorage(device: IDevice) {
    localStorage.setItem('device', JSON.stringify(device));
    this.localDeviceUpdated.next();
  }

  isTheaterOperationInProgress() {
    const isTheaterOperationInProgress = localStorage.getItem(this.theaterOperationInProgress);

    if (!isTheaterOperationInProgress) {
      return false;
    }

    return JSON.parse(isTheaterOperationInProgress);
  }

  startTheaterOperation(patientId: number, patientTheatreVisitId: number): Observable<any> {
    localStorage.setItem(this.theaterOperationInProgress, JSON.stringify(true));
    return this.http.post(`${this.conf.config.baseUrl}/patienttheatre/starttheatretimer/${patientId}/${patientTheatreVisitId}`, null);
  }

  setProcedureNotInProgress() {
    localStorage.setItem(this.theaterOperationInProgress, JSON.stringify(false));
  }

  getTheatreTimeInOperatingRoom(patientId: number, patientTheatreVisitId: number): Observable<number> {
    return this.http.get<number>(
      `${this.conf.config.baseUrl}/patienttheatre/getTheatreTimeInOperatingRoom/${patientId}/${patientTheatreVisitId}`
    );
  }

  buildDeviceName(device: IDevice) {
    switch (device.deviceType) {
      case DeviceType.BackOffice:
        return 'Back Office';
      case DeviceType.Default:
        return 'Default';
      case DeviceType.EmergencyCupboard:
        return 'Emergency Cupboard';
      case DeviceType.TheatreWaiting:
        return 'Theatre Waiting';
      case DeviceType.TheatreOperatingRoom:
        return 'Operating Room';
      case DeviceType.TheatreRecovery:
        return 'Recovery';
      case DeviceType.Pharmacy:
        return 'Pharmacy';
      default:
        break;
    }
  }

  private removeIfCurrentDevice(device: IDevice) {
    if (device.id === this.getDeviceFromLocalStorage().id) {
      const storedDevice = this.getDeviceFromLocalStorage();
      storedDevice.isRemoved = true;
      this.saveDeviceToLocalStorage(storedDevice);
    }
  }

  sendHealth(correlationId: string, healthInformation: IHealthInformation[]) {
    const isHealthy = healthInformation.filter((x) => !x.isHealthy).length === 0;

    const body = {
      deviceId: this.getDeviceId(),
      correlationId: correlationId,
      isHealthy,
      healthInformation,
    };

    return this.http.post(`${this.conf.config.baseUrl}/devices/devicehealth`, body);
  }
}
